import { useState, useEffect, useRef } from 'react';
import { Linkedin, ExternalLink, Loader2, CheckCircle, XCircle, Plus, Trash2, AlertCircle, RefreshCw, PowerOff } from 'lucide-react';
import { getLinkedInAccounts, connectLinkedIn, disconnectLinkedIn, deleteLinkedInAccount } from '../../services/settingsService';
import { useAuth } from '../../contexts/AuthContext';
import { canDisconnectConnection } from '../../utils/permissions';
import type { LinkedInConnection } from '../../types/settings';

const MAX_LINKEDIN_ACCOUNTS = 15;
const STORAGE_KEY = 'oauth-result-linkedin';

export function LinkedInConnectionCard() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<LinkedInConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const loadAccounts = async () => {
    setLoading(true);
    const data = await getLinkedInAccounts();
    setAccounts(data);
    setLoading(false);
  };

  const cleanupPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    loadAccounts();
    return () => {
      cleanupPolling();
    };
  }, []);

  const activeAccounts = accounts.filter(a => a.is_active);
  const isAtLimit = activeAccounts.length >= MAX_LINKEDIN_ACCOUNTS;

  const handleConnect = async () => {
    if (isAtLimit) {
      setError(`Maximum of ${MAX_LINKEDIN_ACCOUNTS} LinkedIn accounts reached`);
      return;
    }
    setConnecting(true);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);

    const result = await connectLinkedIn();

    if (result.success && result.url) {
      const w = 600;
      const h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        result.url,
        'linkedin-auth',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );

      cleanupPolling();

      pollRef.current = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (!raw) {
            cleanupPolling();
            setConnecting(false);
            return;
          }
        }

        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;

        cleanupPolling();
        localStorage.removeItem(STORAGE_KEY);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            setSuccessMessage('LinkedIn account connected successfully!');
            setTimeout(() => setSuccessMessage(null), 5000);
            loadAccounts();
          } else {
            setError(data.error || 'LinkedIn connection failed. Please try again.');
          }
        } catch {
          setError('LinkedIn connection failed. Please try again.');
        }
        setConnecting(false);
      }, 500);

      timeoutRef.current = window.setTimeout(() => {
        cleanupPolling();
        setConnecting(false);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } else {
      setError(result.error || 'Failed to start LinkedIn connection');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (account: LinkedInConnection) => {
    const name = account.linkedin_profile_name || 'this LinkedIn account';
    if (!confirm(`Are you sure you want to disconnect ${name}?`)) return;

    try {
      setDisconnectingId(account.id);
      const success = await disconnectLinkedIn(account.id);
      if (success) {
        await loadAccounts();
        setSuccessMessage('LinkedIn account disconnected');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to disconnect account');
      }
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleDelete = async (account: LinkedInConnection) => {
    const name = account.linkedin_profile_name || 'this LinkedIn account';
    if (!confirm(`Are you sure you want to permanently delete ${name}? This cannot be undone.`)) return;

    try {
      setDeletingId(account.id);
      const success = await deleteLinkedInAccount(account.id);
      if (success) {
        await loadAccounts();
        setSuccessMessage('LinkedIn account deleted');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('Failed to delete account');
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl p-12 text-center">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin mx-auto" />
        <p className="text-gray-500 mt-4">Loading LinkedIn accounts...</p>
      </div>
    );
  }

  const hasConnected = accounts.length > 0;

  if (!hasConnected) {
    return (
      <div className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-bold text-gray-900 font-display mb-2">Why connect LinkedIn?</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Automate personalized connection requests</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Engage with prospects through messaging</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Expand your network on autopilot</span>
              </li>
            </ul>
          </div>

          <button
            onClick={handleConnect}
            disabled={connecting}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {connecting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <Linkedin className="w-5 h-5" />
                <span>Connect LinkedIn</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2.5">
          <CheckCircle className="w-4 h-4 text-[#1875FF] flex-shrink-0" />
          <div>
            <span className="text-xs font-bold text-gray-900 font-display">LinkedIn Automation Active</span>
            <span className="text-xs text-gray-500 ml-1.5">
              &mdash; Agents send connection requests, messages, and engage with your network.
            </span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Profile
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Country
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Type
                </th>
                <th className="text-left px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans">
                  Status
                </th>
                <th className="w-16 px-4 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white/50">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-[#1875FF]/5 transition-all duration-200">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2.5">
                      <Linkedin className="w-4 h-4 text-[#1875FF] flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900">
                        {account.linkedin_profile_name || 'LinkedIn User'}
                      </span>
                      {account.linkedin_profile_url && (
                        <a
                          href={account.linkedin_profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-gray-400 hover:text-[#1875FF] transition-colors"
                          title="View LinkedIn Profile"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700">{account.country || ''}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-700 capitalize">{account.account_type || ''}</span>
                  </td>
                  <td className="px-6 py-4">
                    {account.is_active ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={handleConnect}
                        disabled={connecting}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white hover:shadow-md transition-all disabled:opacity-50"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Reconnect
                      </button>
                    )}
                  </td>
                  <td className="pr-8 py-4 text-right">
                    {canDisconnectConnection(user, account.user_id) && (
                      account.is_active ? (
                        <button
                          onClick={() => handleDisconnect(account)}
                          disabled={disconnectingId === account.id}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Disconnect"
                        >
                          {disconnectingId === account.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <PowerOff className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDelete(account)}
                          disabled={deletingId === account.id}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete"
                        >
                          {deletingId === account.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isAtLimit ? (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Maximum of {MAX_LINKEDIN_ACCOUNTS} LinkedIn accounts reached. Disconnect an account to add a new one.
          </p>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Connecting...</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span className="text-sm">Connect Another Account</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
